<template>
  <v-dialog
    :value="visiblePopup"
    :width="600"
    @click:outside="onCloseClickButton"
    @keydown.esc="onCloseClickButton"
  >
    <v-card class="change-sensitivity-popup popup-wrapper">
      <div class='close-button-section'>
        <span class='close_btn' @click='onCloseClickButton'>
          <span class="material-icons-outlined">
            close
          </span>
        </span>
      </div>
      <div class='title'> {{ $t('sensitivity.asset.popup.title') }} </div>
      <v-slider
        v-model='sensitivityUpdatedValue'
        :tick-labels="sensitivityLabels"
        :min="minValue"
        :max="maxValue"
        :step='1'
        ticks="always"
        tick-size="4"
      >
      </v-slider>
      <v-alert
        text
        dense
        color="#2459d5"
        icon="info"
        border="left"
        class='not-valid-message'
        v-if='!disableSaveButton'
      >
        {{ $t('infrastructure.update.sensitivity.note.popup') }}
      </v-alert>
      <div class='actions'>
        <div class='btn-section'>
          <v-btn
            small
            depressed
            class="cancel-button"
            @click="onCloseClickButton"
          >
            {{ cancelSensitivityButtonCaption }}
          </v-btn>
          <v-btn small depressed class="update-button" :disabled="disableSaveButton" @click="updateSensitivity()">
            {{ updateSensitivityButtonCaption }}
          </v-btn>
        </div>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
import { CONFIGURATION } from '@/constants/configuration';
import disableOuterScrollOnPopupVisible from '@/mixins/disableOuterScrollOnPopupVisible';

export default {
  name: 'ChangeSensitivityPopup',
  mixins: [disableOuterScrollOnPopupVisible],
  props: {
    visiblePopup: {
      type: Boolean,
      required: true,
    },
    sensitivityIndex: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      sensitivityUpdatedValue: this.sensitivityIndex,
    };
  },
  computed: {
    updateSensitivityButtonCaption() {
      return this.$t('update.sensitivity.domain.popup.button');
    },
    cancelSensitivityButtonCaption() {
      return this.$t('cancel.sensitivity.domain.popup.button');
    },
    sensitivityLabels() {
      return Object.values(CONFIGURATION.InfrastructureSensitivityOptions).map(item => this.$t(`asset.sensitivity.${item}`));
    },
    sensitivityValues() {
      return this.sensitivityLabels.map((item, index) => index);
    },
    minValue() {
      return this.sensitivityValues[0] || 0;
    },
    maxValue() {
      return this.sensitivityValues.slice(-1)[0];
    },
    disableSaveButton() {
      return this.sensitivityIndex === this.sensitivityUpdatedValue;
    },
  },
  methods: {
    onCloseClickButton() {
      this.$emit('close');
    },
    updateSensitivity() {
      this.$emit('updateSensitivity', this.sensitivityLabels[this.sensitivityUpdatedValue]);
    },
  },
};
</script>

<style lang='scss'>
@import '~@/assets/styles/design-system';

.change-sensitivity-popup {
  .v-slider__tick-label {
    padding: $space-1 $space-2;
    width: rem(90);
    text-align: center;
    text-transform: capitalize !important;
    font-size: $font-size-base;
    font-weight: bold;
  }
  .v-slider__tick:first-of-type {
    .v-slider__tick-label {
      color: var(--severity-informative) !important;
      background-color: var(--severity-informative-label-bg) !important;
      margin-left: rem(-40);
    }
  }
  .v-slider__tick:nth-of-type(2) {
    .v-slider__tick-label {
      color: var(--severity-low) !important;
      background-color: var(--severity-low-label-bg) !important;
    }
  }

  .v-slider__tick:nth-of-type(3) {
    .v-slider__tick-label {
      color: var(--severity-medium) !important;
      background-color: var(--severity-medium-label-bg) !important;
    }
  }

  .v-slider__tick:nth-of-type(4) {
    .v-slider__tick-label {
      color: var(--severity-high) !important;
      background-color: var(--severity-high-label-bg) !important;
    }
  }
  .v-slider__tick:last-of-type {
    .v-slider__tick-label {
      color: var(--severity-critical) !important;
      background-color: var(--severity-critical-label-bg) !important;
      margin-left: rem(40);
    }
  }
  .v-slider {
    margin-bottom: rem(10);
    .v-slider__tick-label {
      margin-top: rem(12);
    }
  }
}
</style>

<style lang='scss' scoped>
@import '~@/assets/styles/design-system';

.change-sensitivity-popup {
  .not-valid-message {
    font-size: $font-size-xs;
    font-weight: 600;
    letter-spacing: 1.12px;
    line-height: $space-4;
    white-space: pre-line;
  }
  .v-slider {
    .v-slider__tick-label{
      margin-top: rem(12);
    }
  }
  .v-alert {
    margin-top: rem(14);
    width: rem(563);
    margin-left: rem(-33);
  }
  &.popup-wrapper {
    width: rem(600);
    padding: rem(24) rem(50);
    display: flex;
    flex-direction: column;
    .actions {
      display: flex;
      justify-content: flex-end;
      align-items: center;
      margin-top: $space-5;
      margin-right: rem(-32);
      .btn-section {
        display: flex;
        justify-content: flex-end;
        .update-button {
          height: $space-8;
          width: rem(51);
          border-radius: $radius-sm;
          background-color: var(--brand-primary);
          box-shadow: $shadow-sm;
          color: var(--text-inverse) !important;
          font-size: $font-size-sm;
          font-weight: 500;
          letter-spacing: 1.07px;
          line-height: $space-4;
          text-align: center;
          margin-left: $space-2;
        }
        .cancel-button {
          box-sizing: border-box;
          height: $space-8;
          width: rem(86);
          border: $border-width-thin solid var(--brand-primary);
          color: var(--brand-primary);
          border-radius: $radius-sm;
          background-color: var(--bg-primary);
          box-shadow: $shadow-sm;
        }
      }
    }
    .close-button-section {
      z-index: 2;
      margin-right: rem(-32);
      .close_btn {
        float: right;
        &:hover {
          cursor: pointer;
          color: var(--brand-primary);
        }
      }
    }
    .title {
      padding-bottom: rem(35);
      margin-top: rem(-28);
      margin-left: rem(-32);
      color: var(--text-primary);
      font-family: $font-family-bold;
      font-size: rem(24);
      font-weight: bold;
      line-height: $font-size-xl;
      letter-spacing: 0;
    }
    .actions {
      display: flex;
      justify-content: flex-end;
      align-items: center;
      .btn-section {
        display: flex;
        justify-content: flex-end;
      }
    }
  }
}
</style>
